import type { Message, RealtimeChannel } from "ably";
import { getChannel } from "./client";
import { ABLY_CONFIG } from "./config";
import type { PublishGuardState } from "./types";

type AblyMessage = {
	name: string;
	data: unknown;
};

type ConfirmSendOptions = {
	timeoutMs?: number;
	channelName?: string | null;
	channel?: RealtimeChannel | null;
	connectionGuard?: PublishGuardState;
	createConnectionError?: () => Error;
	createTimeoutError?: () => Error;
	responseName?: string;
	isExpectedResponse?: (message: Message) => boolean;
};

export function confirmSend(message: AblyMessage, options: ConfirmSendOptions = {}): Promise<void> {
	const {
		timeoutMs = ABLY_CONFIG.confirmSendTimeoutMs,
		channel: existingChannel,
		channelName,
		connectionGuard,
		createConnectionError,
		createTimeoutError,
		responseName,
		isExpectedResponse,
	} = options;

	if (connectionGuard?.isReady && !connectionGuard.isConnected) {
		return Promise.reject(createConnectionError ? createConnectionError() : new Error("Ably peer is not connected"));
	}

	if (existingChannel) {
		return awaitConfirmation(existingChannel, message, timeoutMs, {
			responseName,
			isExpectedResponse,
			createTimeoutError,
		});
	}

	if (!channelName) {
		return Promise.reject(new Error("Could not resolve Ably channel name"));
	}

	try {
		const channel = getChannel(channelName);
		return awaitConfirmation(channel, message, timeoutMs, {
			responseName,
			isExpectedResponse,
			createTimeoutError,
		});
	} catch {
		return Promise.reject(new Error("Ably client not initialized. Ensure AblyProvider has mounted."));
	}
}

function awaitConfirmation(
	channel: RealtimeChannel,
	message: AblyMessage,
	timeoutMs: number,
	options: Pick<ConfirmSendOptions, "createTimeoutError" | "isExpectedResponse" | "responseName">,
): Promise<void> {
	return new Promise((resolve, reject) => {
		let settled = false;
		const eventName = options.responseName ?? message.name;
		const done = (cb: () => void) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			channel.unsubscribe(eventName, handler);
			cb();
		};

		const handler = (msg: Message) => {
			if (!options.isExpectedResponse || options.isExpectedResponse(msg)) {
				done(() => resolve());
			}
		};

		const timer = setTimeout(() => {
			done(() =>
				reject(options.createTimeoutError ? options.createTimeoutError() : new Error("Ably confirm-send timed out")),
			);
		}, timeoutMs);

		channel.subscribe(eventName, handler);
		channel.publish(message.name, message.data).catch((err: Error) => {
			done(() => reject(err));
		});
	});
}
